import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { Save, Check, AlertCircle, AlertTriangle, ShieldCheck } from 'lucide-react';
import { Eyebrow, PageLoader } from '../components/ui';
import { api, isSampleMode } from '../api/client';
import { useStore } from '../store';
import { useOrganisations } from '../useOrganisations';
import { CustomDimensionsEditor } from './CustomDimensionsEditor';
import type {
  AssessmentMode,
  AssessmentStructureSettingsView,
  AuditEventView,
  BrandingSettingsView,
  CampaignMeta,
  ExperienceSettingsView,
  PlatformSettingsView,
} from '../types';

const input: CSSProperties = { width: '100%', fontSize: 13, padding: '8px 10px', borderRadius: 6, border: '1px solid #E2E2E6', boxSizing: 'border-box', fontFamily: 'inherit' };
const label: CSSProperties = { fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#75757F', marginBottom: 5, display: 'block' };
const card: CSSProperties = { padding: 24, marginBottom: 14 };
const fieldRow: CSSProperties = { display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 14 };

/** Defaults shown in sample mode (no backend) so the surface is fully demonstrable. */
const SAMPLE_SETTINGS: PlatformSettingsView = {
  experience: { version: 0, updatedAt: '', defaultMode: 'quick', voiceEnabled: true, wsStreamingEnabled: true, quickModeTimeCopy: 'About 5 minutes', conversationalTimeCopy: 'About 25 to 35 minutes' },
  assessmentStructure: {
    version: 0, updatedAt: '',
    questionsPerTurn: { min: 1, max: 3 }, skips: { perStage: 2, total: 5 },
    bands: [
      { key: 'early_stage', minScore: 0, maxScore: 24 }, { key: 'developing', minScore: 25, maxScore: 49 },
      { key: 'capable', minScore: 50, maxScore: 74 }, { key: 'advanced', minScore: 75, maxScore: 89 },
      { key: 'transformative', minScore: 90, maxScore: 100 },
    ],
    criteria: [
      { key: 'prompt_mastery', weight: 0.22 }, { key: 'technical_understanding', weight: 0.18 },
      { key: 'practical_application', weight: 0.24 }, { key: 'critical_evaluation', weight: 0.2 }, { key: 'workflow_design', weight: 0.16 },
    ],
    fourDGateThreshold: 200, stage1PassMinScore: 35,
  },
  branding: { version: 0, updatedAt: '', productName: 'FluentAI', colours: { primary: '#00D700', accent: '#2438E6', code: '#000000' } },
};

function friendlyError(e: unknown): string {
  const msg = (e as Error).message ?? 'Could not save.';
  // Network/transport failures surface as raw browser strings ("NetworkError when attempting to
  // fetch resource", "Failed to fetch") or our sample-mode guard. Translate them into something
  // human rather than showing the browser's wording.
  if (/networkerror|failed to fetch|load failed|no api configured/i.test(msg)) {
    return 'The settings service is unreachable. Check your connection and try again.';
  }
  const m = msg.match(/\{[\s\S]*\}/);
  if (m) { try { const p = JSON.parse(m[0]) as { error?: { message?: string } }; if (p.error?.message) return p.error.message; } catch { /* ignore */ } }
  return msg;
}

type Tab = 'experience' | 'campaigns' | 'structure' | 'branding' | 'audit';
const TABS: { key: Tab; label: string }[] = [
  { key: 'experience', label: 'Experience' },
  { key: 'campaigns', label: 'Organisations' },
  { key: 'structure', label: 'Assessment structure' },
  { key: 'branding', label: 'Branding' },
  { key: 'audit', label: 'Audit log' },
];

const SAMPLE_AUDIT: AuditEventView[] = [
  { tsMs: Date.parse('2026-06-29T09:14:00Z'), eventType: 'settings.changed', actor: 'jordan.mercer', target: 'experience', detail: 'version 2' },
  { tsMs: Date.parse('2026-06-28T16:02:00Z'), eventType: 'roster.identified.viewed', actor: 'jordan.mercer', target: 'versent', detail: '23 participants' },
  { tsMs: Date.parse('2026-06-27T11:48:00Z'), eventType: 'dimensions.published', actor: 'jordan.mercer', target: 'acme', detail: 'version 1' },
  { tsMs: Date.parse('2026-06-26T08:30:00Z'), eventType: 'campaign.created', actor: 'jordan.mercer', target: 'q3-uplift', detail: 'Q3 Uplift' },
  { tsMs: Date.parse('2026-06-25T14:05:00Z'), eventType: 'dsar.erased', actor: 'participant', target: 'a-2291', detail: '6 items' },
];

const EVENT_LABEL: Record<string, string> = {
  'settings.changed': 'Settings changed',
  'roster.identified.viewed': 'Identified roster viewed',
  'dimensions.published': 'Dimensions published',
  'campaign.created': 'Organisation created',
  'campaign.updated': 'Organisation updated',
  'dsar.erased': 'Data erased (DSAR)',
  'retention.disposed': 'Retention disposal',
};

/** A publish button + inline error/saved affordance, shared by every tab. */
function PublishBar({ busy, error, saved, version, onPublish, label: btnLabel = 'Publish' }: {
  busy: boolean; error: string | null; saved: boolean; version: number; onPublish: () => void; label?: string;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 18, paddingTop: 16, borderTop: '1px solid #EFEFF2' }}>
      <div style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 12 }}>
        {error && <span style={{ fontSize: 12.5, color: '#C0392B', display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: 420 }}><AlertCircle size={14} style={{ flex: 'none' }} /> {error}</span>}
        {saved && !error && <span className="text-green" style={{ fontSize: 12.5, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}><Check size={14} /> Published v{version}</span>}
        <button type="button" onClick={onPublish} disabled={busy} className="bg-code text-white" style={{ border: 'none', fontSize: 13, fontWeight: 700, borderRadius: 6, padding: '9px 16px', cursor: busy ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7 }}>
          <Save size={14} /> {busy ? 'Publishing…' : btnLabel}
        </button>
      </div>
    </div>
  );
}

function usePublish<T>(scope: 'experience' | 'assessment-structure' | 'branding', onDone?: (s: PlatformSettingsView) => void) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const publish = async (body: T) => {
    setBusy(true); setError(null); setSaved(false);
    try {
      if (!isSampleMode) {
        const { settings } = await api.publishSettings(scope, body);
        onDone?.(settings);
      }
      setSaved(true);
    } catch (e) { setError(friendlyError(e)); } finally { setBusy(false); }
  };
  return { busy, error, saved, publish, clear: () => { setSaved(false); setError(null); } };
}

// ---------------------------------------------------------------------------
function ExperienceTab({ initial, onDone, reloadBranding }: { initial: ExperienceSettingsView; onDone: (s: PlatformSettingsView) => void; reloadBranding: () => void }) {
  const [v, setV] = useState(initial);
  const { busy, error, saved, publish, clear } = usePublish<Omit<ExperienceSettingsView, 'version' | 'updatedAt' | 'updatedBy'>>('experience', (s) => { onDone(s); reloadBranding(); });
  const upd = (patch: Partial<ExperienceSettingsView>) => { clear(); setV((c) => ({ ...c, ...patch })); };

  return (
    <div className="bg-white border border-panel rounded-card" style={card}>
      <Eyebrow>Experience & ops</Eyebrow>
      <div className="text-storm" style={{ fontSize: 13, margin: '3px 0 18px' }}>
        How new assessments behave by default. Changing these takes effect for new sessions; assessments already in progress are unaffected. {initial.version > 0 ? `Published v${initial.version}.` : 'Defaults.'}
      </div>

      <div style={fieldRow}>
        <div style={{ flex: '1 1 220px' }}>
          <label style={label}>Default assessment mode</label>
          <select aria-label="Default assessment mode" style={input} value={v.defaultMode} onChange={(e) => upd({ defaultMode: e.target.value as AssessmentMode })}>
            <option value="quick">Quick (6-step walkthrough)</option>
            <option value="conversational">Conversational (Avery interview)</option>
          </select>
        </div>
        <div style={{ flex: '1 1 160px' }}>
          <label style={label}>Voice input</label>
          <select style={input} value={v.voiceEnabled ? 'on' : 'off'} onChange={(e) => upd({ voiceEnabled: e.target.value === 'on' })}>
            <option value="on">Enabled</option><option value="off">Disabled</option>
          </select>
        </div>
        <div style={{ flex: '1 1 160px' }}>
          <label style={label}>Token streaming (WebSocket)</label>
          <select style={input} value={v.wsStreamingEnabled ? 'on' : 'off'} onChange={(e) => upd({ wsStreamingEnabled: e.target.value === 'on' })}>
            <option value="on">Enabled</option><option value="off">Disabled</option>
          </select>
        </div>
      </div>

      <div style={fieldRow}>
        <div style={{ flex: '1 1 240px' }}>
          <label style={label}>Quick-mode time estimate</label>
          <input style={input} value={v.quickModeTimeCopy} onChange={(e) => upd({ quickModeTimeCopy: e.target.value })} />
        </div>
        <div style={{ flex: '1 1 240px' }}>
          <label style={label}>Conversational time estimate</label>
          <input style={input} value={v.conversationalTimeCopy} onChange={(e) => upd({ conversationalTimeCopy: e.target.value })} />
        </div>
      </div>

      <div style={fieldRow}>
        <div style={{ flex: '0 0 160px' }}>
          <label style={label}>Spend soft alert (USD)</label>
          <input style={input} type="number" min={0} value={v.spendThresholds?.softUsd ?? ''} onChange={(e) => upd({ spendThresholds: { ...v.spendThresholds, softUsd: e.target.value === '' ? undefined : Number(e.target.value) } })} />
        </div>
        <div style={{ flex: '0 0 160px' }}>
          <label style={label}>Spend hard alert (USD)</label>
          <input style={input} type="number" min={0} value={v.spendThresholds?.hardUsd ?? ''} onChange={(e) => upd({ spendThresholds: { ...v.spendThresholds, hardUsd: e.target.value === '' ? undefined : Number(e.target.value) } })} />
        </div>
      </div>

      <PublishBar busy={busy} error={error} saved={saved} version={initial.version + 1} onPublish={() => void publish({
        defaultMode: v.defaultMode, voiceEnabled: v.voiceEnabled, wsStreamingEnabled: v.wsStreamingEnabled,
        quickModeTimeCopy: v.quickModeTimeCopy, conversationalTimeCopy: v.conversationalTimeCopy,
        ...(v.spendThresholds && (v.spendThresholds.softUsd != null || v.spendThresholds.hardUsd != null) ? { spendThresholds: v.spendThresholds } : {}),
      })} />
    </div>
  );
}

// ---------------------------------------------------------------------------
function BrandingTab({ initial, onDone }: { initial: BrandingSettingsView; onDone: (s: PlatformSettingsView) => void }) {
  const [v, setV] = useState(initial);
  const loadPublicSettings = useStore((s) => s.loadPublicSettings);
  const { busy, error, saved, publish, clear } = usePublish<Omit<BrandingSettingsView, 'version' | 'updatedAt' | 'updatedBy'>>('branding', (s) => { onDone(s); loadPublicSettings(); });
  const upd = (patch: Partial<BrandingSettingsView>) => { clear(); setV((c) => ({ ...c, ...patch })); };
  const col = (k: 'primary' | 'accent' | 'code', v2: string) => upd({ colours: { ...v.colours, [k]: v2 } });

  return (
    <div className="bg-white border border-panel rounded-card" style={card}>
      <Eyebrow>Branding</Eyebrow>
      <div className="text-storm" style={{ fontSize: 13, margin: '3px 0 18px' }}>
        Make this deployment your own. The product name and palette apply across the app; capability band colours stay fixed so results remain comparable. {initial.version > 0 ? `Published v${initial.version}.` : 'Defaults.'}
      </div>

      <div style={fieldRow}>
        <div style={{ flex: '2 1 240px' }}>
          <label style={label}>Product name</label>
          <input aria-label="Product name" style={input} value={v.productName} maxLength={60} onChange={(e) => upd({ productName: e.target.value })} />
        </div>
        <div style={{ flex: '2 1 240px' }}>
          <label style={label}>Logo URL (https or /path)</label>
          <input style={input} value={v.logoUrl ?? ''} placeholder="/brand/logo.svg" onChange={(e) => upd({ logoUrl: e.target.value || undefined })} />
        </div>
      </div>

      <div style={fieldRow}>
        {(['primary', 'accent', 'code'] as const).map((k) => (
          <div key={k} style={{ flex: '0 0 150px' }}>
            <label style={label}>{k} colour</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="color" value={v.colours[k]} onChange={(e) => col(k, e.target.value)} style={{ width: 38, height: 34, padding: 0, border: '1px solid #E2E2E6', borderRadius: 6 }} />
              <input style={{ ...input, width: 96 }} value={v.colours[k]} onChange={(e) => col(k, e.target.value)} />
            </div>
          </div>
        ))}
      </div>

      <div style={fieldRow}>
        <div style={{ flex: '1 1 100%' }}>
          <label style={label}>Participant consent copy (optional)</label>
          <textarea style={{ ...input, minHeight: 56, resize: 'vertical' }} maxLength={600} value={v.consentCopy ?? ''} onChange={(e) => upd({ consentCopy: e.target.value || undefined })} />
        </div>
      </div>

      <PublishBar busy={busy} error={error} saved={saved} version={initial.version + 1} onPublish={() => void publish({
        productName: v.productName, ...(v.logoUrl ? { logoUrl: v.logoUrl } : {}), colours: v.colours,
        ...(v.consentCopy ? { consentCopy: v.consentCopy } : {}), ...(v.noticeCopy ? { noticeCopy: v.noticeCopy } : {}),
      })} />
    </div>
  );
}

// ---------------------------------------------------------------------------
function StructureTab({ initial, onDone }: { initial: AssessmentStructureSettingsView; onDone: (s: PlatformSettingsView) => void }) {
  const [v, setV] = useState(initial);
  const [ack, setAck] = useState(false);
  const { busy, error, saved, publish, clear } = usePublish<Record<string, unknown>>('assessment-structure', onDone);
  const upd = (patch: Partial<AssessmentStructureSettingsView>) => { clear(); setV((c) => ({ ...c, ...patch })); };

  return (
    <div className="bg-white border border-panel rounded-card" style={card}>
      <Eyebrow>Assessment structure</Eyebrow>
      <div className="text-storm" style={{ fontSize: 13, margin: '3px 0 14px' }}>
        The scoring gates, band thresholds and criteria weights. {initial.version > 0 ? `Published v${initial.version}.` : 'Built-in defaults (v0).'}
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', background: '#FDECEA', border: '1px solid #F5C6C2', borderRadius: 8, padding: '12px 14px', marginBottom: 18 }}>
        <AlertTriangle size={18} style={{ color: '#C0392B', flex: 'none', marginTop: 1 }} />
        <div style={{ fontSize: 12.5, color: '#7A271F', lineHeight: 1.5 }}>
          Changing assessment structure affects score comparability across cohorts. New assessments are scored under the updated structure; assessments already in progress and completed assessments keep the structure version they were scored under (shown on each result for provenance). Confirm below before publishing.
        </div>
      </div>

      <div style={fieldRow}>
        <div style={{ flex: '0 0 200px' }}>
          <label style={label}>Stage-1 pass gate (0–100)</label>
          <input style={input} type="number" min={0} max={100} value={v.stage1PassMinScore} onChange={(e) => upd({ stage1PassMinScore: Number(e.target.value) })} />
        </div>
        <div style={{ flex: '0 0 200px' }}>
          <label style={label}>4D gate threshold (0–400)</label>
          <input style={input} type="number" min={0} max={400} value={v.fourDGateThreshold} onChange={(e) => upd({ fourDGateThreshold: Number(e.target.value) })} />
        </div>
        <div style={{ flex: '0 0 150px' }}>
          <label style={label}>Questions / turn (min–max)</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input style={{ ...input, width: 60 }} type="number" min={1} value={v.questionsPerTurn.min} onChange={(e) => upd({ questionsPerTurn: { ...v.questionsPerTurn, min: Number(e.target.value) } })} />
            <input style={{ ...input, width: 60 }} type="number" min={1} value={v.questionsPerTurn.max} onChange={(e) => upd({ questionsPerTurn: { ...v.questionsPerTurn, max: Number(e.target.value) } })} />
          </div>
        </div>
      </div>

      <label style={label}>Criteria weights</label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
        {v.criteria.map((c, i) => (
          <div key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span className="text-storm" style={{ fontSize: 13, flex: '1 1 220px' }}>{c.key.replace(/_/g, ' ')}</span>
            <input style={{ ...input, width: 100 }} type="number" min={0} max={1} step={0.01} value={c.weight}
              onChange={(e) => upd({ criteria: v.criteria.map((x, j) => (j === i ? { ...x, weight: Number(e.target.value) } : x)) })} />
          </div>
        ))}
      </div>

      <label style={label}>Band thresholds (min–max, must be contiguous and cover 0–100)</label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 }}>
        {v.bands.map((b, i) => (
          <div key={b.key} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span className="text-storm" style={{ fontSize: 13, flex: '1 1 220px' }}>{b.key.replace(/_/g, ' ')}</span>
            <input style={{ ...input, width: 80 }} type="number" min={0} max={100} value={b.minScore} onChange={(e) => upd({ bands: v.bands.map((x, j) => (j === i ? { ...x, minScore: Number(e.target.value) } : x)) })} />
            <span className="text-storm">–</span>
            <input style={{ ...input, width: 80 }} type="number" min={0} max={100} value={b.maxScore} onChange={(e) => upd({ bands: v.bands.map((x, j) => (j === i ? { ...x, maxScore: Number(e.target.value) } : x)) })} />
          </div>
        ))}
      </div>

      <label className="text-storm" style={{ fontSize: 12.5, display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 14, cursor: 'pointer' }}>
        <input type="checkbox" checked={ack} onChange={(e) => { clear(); setAck(e.target.checked); }} />
        I understand this changes score comparability across cohorts; new assessments use the updated structure while existing ones keep theirs.
      </label>

      <PublishBar busy={busy || !ack} error={error} saved={saved} version={initial.version + 1} onPublish={() => void publish({
        acknowledgeComparabilityImpact: ack,
        questionsPerTurn: v.questionsPerTurn, skips: v.skips, bands: v.bands, criteria: v.criteria,
        fourDGateThreshold: v.fourDGateThreshold, stage1PassMinScore: v.stage1PassMinScore,
        ...(v.stageRoundCounts ? { stageRoundCounts: v.stageRoundCounts } : {}),
      })} />
    </div>
  );
}

// ---------------------------------------------------------------------------
function CampaignsTab() {
  const { organisations, loading } = useOrganisations();
  const [selected, setSelected] = useState<string | null>(null);
  const [meta, setMeta] = useState<CampaignMeta | undefined>();
  const [k, setK] = useState<number | ''>('');
  const [policy, setPolicy] = useState<'allowed' | 'denied'>('allowed');
  const [override, setOverride] = useState<'' | AssessmentMode>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!selected) { setMeta(undefined); return; }
    if (isSampleMode) { setMeta(undefined); setK(''); setPolicy('allowed'); setOverride(''); return; }
    void (async () => {
      try {
        const { campaign } = await api.getCampaign(selected, true, false);
        setMeta(campaign);
        setK(campaign?.kAnonymityThreshold ?? '');
        setPolicy(campaign?.identifiedRosterPolicy ?? 'allowed');
        setOverride(campaign?.modeOverride ?? '');
      } catch (e) { setError(friendlyError(e)); }
    })();
  }, [selected]);

  const publish = async () => {
    if (!selected) return;
    setBusy(true); setError(null); setSaved(false);
    try {
      if (!isSampleMode) {
        await api.publishCampaignSettings(selected, {
          ...(k !== '' ? { kAnonymityThreshold: Number(k) } : {}),
          identifiedRosterPolicy: policy,
          ...(override ? { modeOverride: override } : {}),
        });
      }
      setSaved(true);
    } catch (e) { setError(friendlyError(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="bg-white border border-panel rounded-card" style={card}>
        <Eyebrow>Per-organisation settings</Eyebrow>
        <div className="text-storm" style={{ fontSize: 13, margin: '3px 0 18px' }}>
          Override platform defaults for one organisation, and author its supplementary dimensions.
        </div>
        <label style={label}>Organisation</label>
        <select style={{ ...input, maxWidth: 360 }} value={selected ?? ''} onChange={(e) => { setSaved(false); setError(null); setSelected(e.target.value || null); }} disabled={loading}>
          <option value="">{loading ? 'Loading…' : 'Select an organisation…'}</option>
          {organisations.map((o) => <option key={o.campaignId} value={o.campaignId}>{o.name}</option>)}
        </select>

        {selected && (
          <>
            <div style={{ ...fieldRow, marginTop: 18 }}>
              <div style={{ flex: '0 0 200px' }}>
                <label style={label}>k-anonymity threshold (≥ 2)</label>
                <input style={input} type="number" min={2} value={k} placeholder="Default 5" onChange={(e) => { setSaved(false); setK(e.target.value === '' ? '' : Number(e.target.value)); }} />
              </div>
              <div style={{ flex: '1 1 200px' }}>
                <label style={label}>Identified roster</label>
                <select style={input} value={policy} onChange={(e) => { setSaved(false); setPolicy(e.target.value as 'allowed' | 'denied'); }}>
                  <option value="allowed">Allowed (admin-only, audit-logged)</option>
                  <option value="denied">Denied (always anonymised)</option>
                </select>
              </div>
              <div style={{ flex: '1 1 200px' }}>
                <label style={label}>Mode override</label>
                <select style={input} value={override} onChange={(e) => { setSaved(false); setOverride(e.target.value as '' | AssessmentMode); }}>
                  <option value="">Use platform default</option>
                  <option value="quick">Quick</option>
                  <option value="conversational">Conversational</option>
                </select>
              </div>
            </div>
            <PublishBar busy={busy} error={error} saved={saved} version={0} onPublish={() => void publish()} label="Save settings" />
          </>
        )}
      </div>

      {selected && (
        <CustomDimensionsEditor campaignId={selected} initial={meta?.customDimensions} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function AuditTab() {
  const [events, setEvents] = useState<AuditEventView[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isSampleMode) { setEvents(SAMPLE_AUDIT); return; }
    void (async () => {
      try { const { events } = await api.getAuditLog(90); setEvents(events); }
      catch (e) { setError(friendlyError(e)); }
    })();
  }, []);

  return (
    <div className="bg-white border border-panel rounded-card" style={card}>
      <Eyebrow>Audit trail</Eyebrow>
      <div className="text-storm" style={{ fontSize: 13, margin: '3px 0 16px' }}>
        Privileged actions over the last 90 days — settings changes, identified-roster views, organisation edits, and data erasures. Read-only; never includes scores or transcript content.
      </div>
      {error && <div style={{ fontSize: 13, color: '#C0392B' }}>{error}</div>}
      {!events && !error && <div className="text-storm" style={{ fontSize: 13 }}>Loading…</div>}
      {events && events.length === 0 && <div className="text-storm" style={{ fontSize: 13 }}>No audit events recorded yet.</div>}
      {events && events.length > 0 && (
        <div role="table" style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          <div role="row" style={{ display: 'grid', gridTemplateColumns: '1.4fr 1.6fr 1fr 1.4fr', gap: 12, padding: '8px 0', borderBottom: '1px solid #EFEFF2', ...label, marginBottom: 0 }}>
            <span>When</span><span>Event</span><span>Actor</span><span>Target / detail</span>
          </div>
          {events.map((e, i) => (
            <div role="row" key={`${e.tsMs}-${i}`} style={{ display: 'grid', gridTemplateColumns: '1.4fr 1.6fr 1fr 1.4fr', gap: 12, padding: '10px 0', borderBottom: '1px solid #F5F5F7', fontSize: 13 }}>
              <span className="text-storm">{new Date(e.tsMs).toLocaleString('en-AU')}</span>
              <span style={{ fontWeight: 600 }}>{EVENT_LABEL[e.eventType] ?? e.eventType}</span>
              <span className="text-storm">{e.actor}</span>
              <span className="text-storm">{[e.target, e.detail].filter(Boolean).join(' · ')}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
/** Graceful, retryable failure state for when the settings service can't be reached, instead of
 *  dumping a raw browser error string under the tabs. */
function SettingsError({ reason, onRetry }: { reason: string; onRetry: () => void }) {
  return (
    <div className="bg-white border border-panel" style={{ borderRadius: 10, padding: '30px 26px', textAlign: 'center', maxWidth: 520, margin: '4px auto 0' }}>
      <AlertTriangle size={22} className="text-storm" style={{ display: 'block', margin: '0 auto 10px' }} />
      <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>Couldn’t load settings</div>
      <p className="text-storm" style={{ fontSize: 13.5, lineHeight: 1.5, margin: '0 0 16px' }}>{reason}</p>
      <button type="button" onClick={onRetry}
        className="bg-code text-white" style={{ fontSize: 13, fontWeight: 700, borderRadius: 8, padding: '9px 20px', border: 'none', cursor: 'pointer' }}>
        Try again
      </button>
    </div>
  );
}

export function Settings() {
  const [tab, setTab] = useState<Tab>('experience');
  const [settings, setSettings] = useState<PlatformSettingsView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    if (isSampleMode) { setSettings(SAMPLE_SETTINGS); setLoading(false); return; }
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const { settings } = await api.getAdminSettings();
        setSettings(settings);
      } catch (e) {
        setError(friendlyError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="bg-subtle text-code" style={{ flex: 1 }}>
      <div className="fui-admin" style={{ maxWidth: 1180, margin: '0 auto', padding: '34px 32px 80px' }}>
        <div className="animate-fadeUp" style={{ marginBottom: 18 }}>
          <span className="text-green-text uppercase tracking-eyebrow" style={{ fontSize: 11, fontWeight: 700 }}>Settings</span>
          <h1 style={{ fontSize: 32, fontWeight: 700, letterSpacing: '-0.03em', margin: '10px 0 0' }}>Platform settings</h1>
          <div className="text-storm" style={{ fontSize: 14, marginTop: 7 }}>
            Configure this deployment without a code change. Edits are versioned and audited.
          </div>
          <button type="button" onClick={() => useStore.getState().setScreen('setup')}
            className="text-green-text" style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, marginTop: 10, padding: 0 }}>
            New here? Run the setup wizard →
          </button>
        </div>

        <div className="bg-white border border-panel" style={{ display: 'flex', alignItems: 'center', gap: 10, borderRadius: 8, padding: '10px 16px', marginBottom: 18 }}>
          <ShieldCheck size={17} className="text-green shrink-0" />
          <span className="text-storm" style={{ fontSize: 13 }}>Changes apply to new sessions; in-progress assessments keep the configuration they began with.</span>
        </div>

        <div role="tablist" style={{ display: 'flex', gap: 6, marginBottom: 18, flexWrap: 'wrap' }}>
          {TABS.map((t) => (
            <button key={t.key} role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)}
              className={tab === t.key ? 'bg-code text-white' : 'bg-white text-storm border border-panel'}
              style={{ fontSize: 13, fontWeight: 600, borderRadius: 8, padding: '8px 14px', cursor: 'pointer' }}>
              {t.label}
            </button>
          ))}
        </div>

        <PageLoader show={loading && !settings} label="Loading settings…" />
        {!loading && error && !settings && <SettingsError reason={error} onRetry={load} />}

        {settings && (
          <>
            {tab === 'experience' && <ExperienceTab initial={settings.experience} onDone={setSettings} reloadBranding={useStore.getState().loadPublicSettings} />}
            {tab === 'branding' && <BrandingTab initial={settings.branding} onDone={setSettings} />}
            {tab === 'structure' && <StructureTab initial={settings.assessmentStructure} onDone={setSettings} />}
            {tab === 'campaigns' && <CampaignsTab />}
            {tab === 'audit' && <AuditTab />}
          </>
        )}
      </div>
    </div>
  );
}
